import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ThemedFixedButton } from "@/Button";
import { Checkbox, ValidateCheckbox } from "@/Checkbox";
import { Header } from "@/Header";
import { ValidateInput } from "@/Input";
import { SequentialAnimation } from "@/SequentialAnimation";
import { Controller, useForm } from "react-hook-form";
import { SignUpRequest } from "#/auth/signUp";
import { useContext, useMemo, useState } from "react";
import { Divider } from "@/Divider";
import { useAppColor } from "^/common/useAppColor";
import { useCookies } from "react-cookie";
import { SignupContext } from "@/SignupContext";
import { useStorage } from "^/common/useStorage";
import { SignType } from "#/unique/domain";
import { userAppLogin, useUserSignUp } from "~/services/auth";
import { Dialog } from "@/Dialog";
import { SHA256 } from "crypto-js";
import { TermsDocContent } from "@/common/TermsDocContent";
import MarketingConsentContent from "@/common/MarketingConsentContent";

export const Route = createFileRoute("/sign-up/customer")({
  component: RouteComponent,
});
export interface SignUpCustomerState extends SignUpRequest {
  nickname: string;
  userName: string;
  phoneNumber: string;
  termsYn: boolean;
  agree2: boolean;
}
function RouteComponent() {
  const [cookie] = useCookies(["email", "name"]);
  const [snsId] = useStorage<string>("local-sns-id");
  const [snsType] = useStorage<SignType>("local-sign-sns-type");
  const [fcm] = useStorage<string>("session-fcm");
  const { setInfo } = useContext(SignupContext);
  const email: string = useMemo(() => cookie.email, [cookie]);
  const name: string = useMemo(() => cookie.name, [cookie]);
  const { history } = useRouter();
  const { mutate: signUp } = useUserSignUp();

  // 모달 상태 관리
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPrivacyModalVisible, setIsPrivacyModalVisible] = useState(false);
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
  const [isMarketingModalVisible, setIsMarketingModalVisible] = useState(false);
  // const [privacyContent, setPrivacyContent] = useState("");
  // const [isLoadingPrivacy, setIsLoadingPrivacy] = useState(false);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handlePrivacyModalClose = () => {
    setIsPrivacyModalVisible(false);
  };

  const handleTermsModalClose = () => {
    setIsTermsModalVisible(false);
  };

  const handleMarketingModalClose = () => {
    setIsMarketingModalVisible(false);
  };

  const handleConfirm = () => {
    setIsModalVisible(false);
    history.push("/sign-up/pet");
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    history.push("/sign-up/customer-done");
  };
  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitted, isValid },
    watch,
  } = useForm<SignUpCustomerState>({
    mode: "onChange",
    defaultValues: {
      email: email,
      userName: name || "",
      phoneNumber: "",
      termsYn: false,
      agree2: false,
      nickname: "",
    },
  });

  const isNicknameDuplicateError = (
    statusCode?: number,
    message?: string
  ) => {
    return statusCode === 4009 && message?.includes("닉네임");
  };

  const allCheck = watch("termsYn") && watch("agree2");
  const handleAllCheck = () => {
    if (isSubmitted || !allCheck) {
      setValue("termsYn", !allCheck, { shouldValidate: true });
      setValue("agree2", !allCheck, { shouldValidate: true });
    } else {
      setValue("termsYn", !allCheck);
      setValue("agree2", !allCheck);
    }
  };
  const bindSubmit = (data: SignUpCustomerState) => {
    setInfo(data);
    clearErrors("nickname");
    console.log(snsType);
    // 사용자 회원가입 API 호출
    const signUpData = {
      email: data.email || email || "",
      userName: data.userName || "",
      phoneNumber: data.phoneNumber || "",
      termsYn: data.termsYn ? "Y" : "N",
      agree2: data.agree2 ? "Y" : "N",
      snsId: snsId || "",
      cdSnsType: snsType || "",
      nickname: data.nickname || "",
    };

    signUp(signUpData, {
      onSuccess: async (response) => {
        console.log("회원가입 성공:", response);
        if (response.statusCode !== 200) {
          if (
            isNicknameDuplicateError(response.statusCode, response.message)
          ) {
            setError(
              "nickname",
              {
                type: "server",
                message: response.message || "이미 존재하는 닉네임 입니다.",
              },
              { shouldFocus: true }
            );
            return;
          }

          history.push("/");
          return;
        }

        const userAgent = navigator.userAgent;
        const isApp = /MT_I|MT_A/i.test(userAgent);

        if (isApp) {
          // 회원가입 성공 후 앱 로그인 API 호출
          const id = snsId || "";
          const key = snsType + id;
          const hash = SHA256(key).toString();
          try {
            const appLoginData = {
              cdSnsType: snsType || "",
              hash: hash,
              fcmToken: fcm || "",
              cdMobileType: /MT_A/i.test(userAgent) ? "ANDROID" : "IOS",
            };
            const loginResponse = await userAppLogin(appLoginData);
            console.log("앱 로그인 성공:", loginResponse);

            // 앱 로그인 성공 시 조건에 따른 리다이렉트
            if (loginResponse?.statusCode === 200) {
              // 로그인 성공 시 비즈니스 메인 페이지로 이동
              const infoData = {
                ...data,
                userSeq: response.data?.userSeq,
              };
              setInfo(infoData);
              showModal();
            } else {
              // 로그인 실패 시 회원가입 완료 페이지로 이동
              // alert("앱 로그인 실패");
              history.push("/");
            }
          } catch (error) {
            console.error("앱 로그인 실패:", error);
            // 앱 로그인이 실패해도 회원가입은 성공했으므로 다음 페이지로 이동
            // history.push("/sign-up/business-owner-done");
          }
        } else {
          if (response.statusCode === 200) {
            console.log(response);
            const infoData = {
              ...data,
              userSeq: response.data?.userSeq,
            };
            setInfo(infoData);
            showModal();
          } else {
            history.push("/");
          }
        }
      },
      onError: (error: any) => {
        console.error("사용자 회원가입 실패:", error);
        if (
          isNicknameDuplicateError(
            error?.response?.data?.statusCode,
            error?.response?.data?.message
          )
        ) {
          setError(
            "nickname",
            {
              type: "server",
              message:
                error?.response?.data?.message ||
                "이미 존재하는 닉네임 입니다.",
            },
            { shouldFocus: true }
          );
        }
        // 에러 발생 시에도 다음 단계로 진행 (임시)
        // history.push("/sign-up/pet");
      },
    });
    // showModal();
  };
  useAppColor({
    background: "#ff9934",
  });

  return (
    <div className="flex flex-col flex-auto">
      <Header title="회원가입" className="text-white border-0 bg-yellow" />
      <form className="flex flex-col flex-auto gap-4 px-5 pt-10 bg-white rounded-t-3xl">
        <SequentialAnimation>
          <ValidateInput
            className="w-full"
            placeholder="이메일 주소를 입력하세요"
            label="이메일 주소"
            value={email}
            disabled
          />
          <ValidateInput
            className="w-full"
            label="이름"
            placeholder="이름을 입력하세요"
            errorMessage={errors?.userName?.message}
            {...register("userName", {
              required: { value: true, message: "이름을 입력하세요" },
              minLength: {
                value: 2,
                message: "이름은 최소 2글자 이상이어야 합니다",
              },
              pattern: {
                value: /^[가-힣]+$/,
                message: "한글만 입력 가능합니다",
              },
            })}
          />
          <Divider className="mt-auto" />

          <ValidateInput
            className="w-full"
            label="휴대폰번호"
            type="tel"
            // maxLength={13}
            placeholder="휴대폰 번호를 숫자만 입력하세요"
            errorMessage={errors?.phoneNumber?.message}
            {...register("phoneNumber", {
              required: { value: true, message: "휴대폰 번호를 입력하세요" },
              pattern: {
                value: /^\d{3}-\d{3,4}-\d{4}$/,
                message: "유효한 휴대폰 번호를 입력하세요",
              },
            })}
          />
          <ValidateInput
            className="w-full"
            label="집사님 닉네임"
            placeholder="집사님 닉네임을 입력하세요."
            errorMessage={errors?.nickname?.message}
            {...register("nickname", {
              required: { value: true, message: "닉네임을 입력하세요" },
              minLength: {
                value: 2,
                message: "닉네임은 최소 2글자 이상이어야 합니다",
              },
              onChange: () => clearErrors("nickname"),
            })}
          />
        </SequentialAnimation>
        <Divider className="mt-auto" />

        <SequentialAnimation initialDelay={0.2}>
          <Checkbox
            title="전체 동의"
            checked={allCheck}
            onChange={handleAllCheck}
          />
          <Divider line />
          <Controller
            name="termsYn"
            control={control}
            rules={{ required: "필수 동의입니다." }}
            render={({ field }) => (
              <ValidateCheckbox
                defaultChecked={field.value}
                onChange={() => field.onChange(!field.value)}
                errorMessage={errors?.termsYn?.message}
                title="[필수] 멍투데이 서비스 이용 약관 및 개인정보처리방침에 동의합니다."
              />
            )}
          />
          <div className="flex gap-2 pl-8">
            <p
              onClick={() => setIsTermsModalVisible(true)}
              className="text-sm mt-0.5 text-orange-400 cursor-pointer"
            >
              [이용약관]
            </p>
            <p
              onClick={() => setIsPrivacyModalVisible(true)}
              className="text-sm mt-0.5 text-orange-400 cursor-pointer"
            >
              [개인정보처리방침]
            </p>
          </div>
          <Controller
            name="agree2"
            control={control}
            render={({ field }) => (
              <ValidateCheckbox
                defaultChecked={field.value}
                onChange={() => field.onChange(!field.value)}
                errorMessage={errors?.agree2?.message}
                title="[선택] 광고성 정보 수신 전체 동의"
                desc="마케팅정보 수집에 동의하시면 유치원 할인, 프로모션 정보 등을 받아보실 수 있습니다."
              />
            )}
          />
          <p
            onClick={() => setIsMarketingModalVisible(true)}
            className="text-sm mt-0.5 text-orange-400 pl-8 cursor-pointer mb-4 pb-2"
          >
            [마케팅정보 수신 동의]
          </p>
          {/* <Checkbox
            title="[선택] 광고성 정보 수신 전체 동의"
            desc="마케팅정보 수집에 동의하시면 유치원 할인, 프로모션 정보 등을 받아보실 수 있습니다."
          /> */}
        </SequentialAnimation>
      </form>
      <ThemedFixedButton
        disabled={!isValid}
        onClick={handleSubmit(bindSubmit)}
        color="primary"
        size="md"
      >
        약관 동의하고 가입하기
      </ThemedFixedButton>

      {/* 펫 등록 확인 모달 */}
      <Dialog
        visible={isModalVisible}
        onClose={handleModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              회원가입 완료
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              지금 펫등록을 진행하겠습니까?
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full"
              onClick={handleCancel}
            >
              다음에 하기
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleConfirm}
            >
              팻등록 하기
            </button>
          </div>
        </div>
      </Dialog>

      {/* 개인정보처리방침 모달 */}
      <Dialog visible={isPrivacyModalVisible} onClose={handlePrivacyModalClose}>
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">개인정보처리방침</h2>
            <button
              onClick={handlePrivacyModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <TermsDocContent targetRole="GUARDIAN" termsType="TERMS_TYPE_PRIVACY" />
          </div>
        </div>
      </Dialog>
      {/* 이용약관 모달 */}
      <Dialog visible={isTermsModalVisible} onClose={handleTermsModalClose}>
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">이용약관</h2>
            <button
              onClick={handleTermsModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <TermsDocContent targetRole="GUARDIAN" termsType="TERMS_TYPE_SERVICE" />
          </div>
        </div>
      </Dialog>
      {/* 마케팅정보 수신 동의 모달 */}
      <Dialog
        visible={isMarketingModalVisible}
        onClose={handleMarketingModalClose}
      >
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">마케팅 정보 수신동의</h2>
            <button
              onClick={handleMarketingModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <MarketingConsentContent />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
